/**
 * Published Reports — dashboard content (publish-report spec, Req 5, 6;
 * refreshable-report spec, Req 4).
 *
 * Left: the reports visible to the reader in the current team (global, team
 * public, or their own), newest first, filterable All / Mine / Team / Global.
 * Right: the selected report — title, description, provenance, question and
 * the answer rendered with the chat's own renderer (marked + AnswerTableDownloads,
 * so tables keep their Download CSV control) — plus Copy link / Edit /
 * Unpublish for readers who may manage it.
 *
 * Refreshable ("rerun") reports add "Refresh now" for managers: the runner
 * Lambda starts a fresh Ask AI turn (startReportRun), this view polls the turn
 * and records the outcome (recordReportRun), which swaps the report's content
 * on success and leaves it untouched on failure. A run history lists past
 * runs. A `running` run left behind by a closed tab is reconciled when the
 * report is next opened (D6): resolved from its stored turn, or marked failed
 * once it is older than the request timeout.
 *
 * Content is fetched only for the selected report (the list document leaves
 * `content` unselected). `?report=<id>` is honoured on mount and written on
 * selection so a report view is bookmarkable (Req 5.6, 5.7).
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { marked, parse } from "marked";
import { generateAuditedClient } from "../../../../audit/auditedClient";
import {
  getPublishedReport,
  getPublishedReportsSummary,
  getReportRuns,
} from "../../../../graphql/queries";
import { startReportRun, unpublishReport } from "../../../../graphql/mutations";
import type {
  GetPublishedReportsInput,
  GetReportRunsInput,
  PublishedReport,
  PublishedReportRun,
  PublishedReportSummary,
  StartReportRunInput,
  UnpublishReportInput,
} from "../../../../models/API";
import type { PluginReportContentProps } from "../../types";
import AnswerTableDownloads from "../../../csvDownload/AnswerTableDownloads";
import ConfirmationDialog from "../../../utils/ConfirmationDialog";
import { covertUtcToLocal } from "../../../utils/formatters";
import {
  canManagePublishedReport,
  canRefresh,
  DEFAULT_RUN_TIMEOUT_SECONDS,
  formatElapsed,
  isRunStale,
  modelLabel,
  parsePublishedReportSource,
  publishedReportDeepLink,
  refreshModeOf,
  runStatusLabel,
  visibilityOf,
  VISIBILITY_STYLE,
} from "../../../publishedReports/publishedReportModel";
import {
  buildRecordRunInput,
  pollReportRun,
  readRunTurn,
  recordRun,
  RunOutcome,
} from "../../../publishedReports/reportRun";
import EditPublishedReportDialog from "./EditPublishedReportDialog";

marked.setOptions({ breaks: true, gfm: true });

type Filter = "all" | "mine" | "team" | "global";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "mine", label: "Mine" },
  { id: "team", label: "Team" },
  { id: "global", label: "Global" },
];

const RUNS_PAGE = 20;

interface ActiveRefresh {
  runId: number;
  runTurnId: string;
  /** Epoch ms; for the elapsed display. */
  startedAt: number;
  /** The user whose session the run was enqueued under. */
  triggeredBy: number;
}

interface PublishedReportsContentProps extends PluginReportContentProps {
  /** How long a refresh is waited for (seconds); defaults to the engine's timeout. */
  runTimeoutSeconds?: number;
  /** Poll interval for a refresh; tests shorten it. */
  pollIntervalMs?: number;
}

function readReportIdFromUrl(): number | null {
  const raw = new URLSearchParams(window.location.search).get("report");
  const id = raw ? Number(raw) : NaN;
  return Number.isInteger(id) && id > 0 ? id : null;
}

function writeReportIdToUrl(id: number | null) {
  const params = new URLSearchParams(window.location.search);
  if (id === null) params.delete("report");
  else params.set("report", String(id));
  window.history.replaceState(
    {},
    "",
    `${window.location.pathname}?${params.toString()}`
  );
}

function errorMessageOf(err: any, fallback: string): string {
  const message = err?.errors?.[0]?.message ?? err?.message;
  return typeof message === "string" && message.length > 0 ? message : fallback;
}

/** Epoch ms of an RDS timestamp ("YYYY-MM-DD HH:MM:SS[.ffffff]", UTC). */
function epochOf(timestamp: string): number {
  const ms = new Date(timestamp.replace(" ", "T") + "Z").getTime();
  return Number.isNaN(ms) ? Date.now() : ms;
}

const PublishedReportsContent: React.FC<PublishedReportsContentProps> = ({
  effectiveRole,
  currentTeamId,
  userId,
  runTimeoutSeconds = DEFAULT_RUN_TIMEOUT_SECONDS,
  pollIntervalMs = 2000,
}) => {
  const client = useMemo(() => generateAuditedClient(), []);
  const [reports, setReports] = useState<PublishedReportSummary[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [selectedId, setSelectedId] = useState<number | null>(() =>
    readReportIdFromUrl()
  );
  const [detail, setDetail] = useState<PublishedReport | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailMissing, setDetailMissing] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Refreshable reports (F2)
  const [runs, setRuns] = useState<PublishedReportRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [refresh, setRefresh] = useState<ActiveRefresh | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  // Ignore responses that arrive for a report the user has navigated away from.
  const detailRequestRef = useRef(0);
  const refreshAbortRef = useRef<AbortController | null>(null);

  const ready = userId !== undefined && currentTeamId !== undefined;

  const loadList = useCallback(async () => {
    if (!ready) return;
    setListLoading(true);
    setListError(null);
    try {
      const input: GetPublishedReportsInput = {
        user_id: userId!,
        team_id: currentTeamId!,
      };
      const result = await client.graphql({
        query: getPublishedReportsSummary,
        variables: { input },
      });
      setReports(result.data.getPublishedReports ?? []);
    } catch (err) {
      console.error("[PublishedReports] list failed:", err);
      setListError(
        errorMessageOf(
          err,
          "Could not load published reports. Please try again."
        )
      );
    } finally {
      setListLoading(false);
    }
  }, [client, ready, userId, currentTeamId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const loadRuns = useCallback(
    async (reportId: number): Promise<PublishedReportRun[]> => {
      if (!ready) return [];
      setRunsLoading(true);
      try {
        const input: GetReportRunsInput = {
          user_id: userId!,
          team_id: currentTeamId!,
          report_id: reportId,
          limit: RUNS_PAGE,
        };
        const result = await client.graphql({
          query: getReportRuns,
          variables: { input },
        });
        const rows: PublishedReportRun[] = result.data.getReportRuns ?? [];
        setRuns(rows);
        return rows;
      } catch (err) {
        console.error("[PublishedReports] runs failed:", err);
        return [];
      } finally {
        setRunsLoading(false);
      }
    },
    [client, ready, userId, currentTeamId]
  );

  // Detail fetch (content included) for one report, inside the scope.
  const loadDetail = useCallback(
    async (reportId: number): Promise<PublishedReport | null> => {
      if (!ready) return null;
      const request = ++detailRequestRef.current;
      setDetailLoading(true);
      setDetailError(null);
      setDetailMissing(false);
      try {
        const input: GetPublishedReportsInput = {
          user_id: userId!,
          team_id: currentTeamId!,
          report_id: reportId,
        };
        const result = await client.graphql({
          query: getPublishedReport,
          variables: { input },
        });
        if (request !== detailRequestRef.current) return null;
        const found: PublishedReport | null =
          (result.data.getPublishedReports ?? [])[0] ?? null;
        setDetail(found);
        setDetailMissing(!found);
        return found;
      } catch (err) {
        console.error("[PublishedReports] detail failed:", err);
        if (request === detailRequestRef.current) {
          setDetail(null);
          setDetailError(
            errorMessageOf(err, "Could not load this report. Please try again.")
          );
        }
        return null;
      } finally {
        if (request === detailRequestRef.current) setDetailLoading(false);
      }
    },
    [client, ready, userId, currentTeamId]
  );

  const canManageReport = useCallback(
    (report: PublishedReport | PublishedReportSummary) =>
      canManagePublishedReport(report, {
        userId,
        effectiveRole,
        currentTeamId,
      }),
    [userId, effectiveRole, currentTeamId]
  );

  /** Record a finished run and reload the report + history. */
  const finishRun = useCallback(
    async (
      report: PublishedReport,
      run: { runId: number; runTurnId: string },
      outcome: RunOutcome
    ) => {
      try {
        await recordRun(
          client,
          buildRecordRunInput({
            userId: userId!,
            reportId: report.id,
            runId: run.runId,
            outcome,
          })
        );
        if (outcome.status === "succeeded") {
          setNotice("Report refreshed");
        } else {
          setRefreshError(
            outcome.status === "failed"
              ? `The refresh failed (${outcome.errorType}). Showing the last successful answer.`
              : "The refresh did not finish in time. Showing the last successful answer."
          );
        }
      } catch (err) {
        console.error("[PublishedReports] recording the run failed:", err);
        setRefreshError(
          errorMessageOf(err, "The refresh finished but could not be recorded.")
        );
      }
      await Promise.all([loadDetail(report.id), loadRuns(report.id)]);
      loadList();
    },
    [client, userId, loadDetail, loadRuns, loadList]
  );

  /** Wait for an in-flight run (ours or an attached one), then record it. */
  const waitForRun = useCallback(
    async (report: PublishedReport, active: ActiveRefresh) => {
      const controller = new AbortController();
      refreshAbortRef.current = controller;
      setRefresh(active);
      const outcome = await pollReportRun(
        client,
        active.triggeredBy,
        active.runTurnId,
        {
          timeoutMs: Math.max(
            1000,
            runTimeoutSeconds * 1000 - (Date.now() - active.startedAt)
          ),
          intervalMs: pollIntervalMs,
          signal: controller.signal,
        }
      );
      if (controller.signal.aborted) return;
      refreshAbortRef.current = null;
      setRefresh(null);
      if (outcome.status === "cancelled") return;
      await finishRun(report, active, outcome);
    },
    [client, runTimeoutSeconds, pollIntervalMs, finishRun]
  );

  /**
   * Reconcile a `running` run on open (D6): attach to it when it is still
   * within the timeout window, otherwise resolve it from its stored turn (or
   * mark it failed) if the viewer may manage the report.
   */
  const reconcile = useCallback(
    async (report: PublishedReport, rows: PublishedReportRun[]) => {
      const running = rows.find((r) => r.status === "running");
      if (!running || !running.run_turn_id || refreshAbortRef.current) return;
      const active: ActiveRefresh = {
        runId: running.id,
        runTurnId: running.run_turn_id,
        startedAt: epochOf(running.started_at),
        triggeredBy: running.triggered_by,
      };
      if (!isRunStale(running.started_at, runTimeoutSeconds)) {
        await waitForRun(report, active);
        return;
      }
      if (!canManageReport(report)) return;
      let outcome: RunOutcome | undefined;
      try {
        outcome = await readRunTurn(
          client,
          running.triggered_by,
          running.run_turn_id
        );
      } catch (err) {
        console.error("[PublishedReports] reconcile read failed:", err);
      }
      await finishRun(
        report,
        active,
        outcome ?? { status: "timeout", runTurnId: running.run_turn_id }
      );
    },
    [client, runTimeoutSeconds, waitForRun, canManageReport, finishRun]
  );

  // Selection -> detail (+ runs and reconcile for refreshable reports).
  useEffect(() => {
    refreshAbortRef.current?.abort();
    refreshAbortRef.current = null;
    setRefresh(null);
    setRefreshError(null);
    setRuns([]);
    if (!ready || selectedId === null) {
      setDetail(null);
      setDetailMissing(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const found = await loadDetail(selectedId);
      if (cancelled || !found || refreshModeOf(found) !== "rerun") return;
      const rows = await loadRuns(found.id);
      if (cancelled) return;
      if (found.last_run_status === "running") reconcile(found, rows);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, selectedId]);

  // Elapsed-time display while a refresh is in flight.
  useEffect(() => {
    if (!refresh) return;
    const startedAt = refresh.startedAt;
    const tick = () => setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [refresh]);

  const handleSelect = (id: number) => {
    setSelectedId(id);
    writeReportIdToUrl(id);
  };

  const handleRefreshNow = async () => {
    if (!detail || userId === undefined || refresh) return;
    setRefreshError(null);
    setNotice(null);
    try {
      const input: StartReportRunInput = {
        user_id: userId,
        report_id: detail.id,
      };
      const result = await client.graphql({
        query: startReportRun,
        variables: { input },
      });
      const started = result.data.startReportRun;
      if (!started || started.run_id === null || started.run_id === undefined)
        throw new Error("No run returned");
      setDetail((prev) =>
        prev && prev.id === detail.id
          ? { ...prev, last_run_status: "running" }
          : prev
      );
      await waitForRun(detail, {
        runId: started.run_id,
        runTurnId: started.run_turn_id,
        startedAt: Date.now(),
        triggeredBy: userId,
      });
    } catch (err) {
      console.error("[PublishedReports] refresh failed to start:", err);
      setRefreshError(
        errorMessageOf(err, "Could not start the refresh. Please try again.")
      );
    }
  };

  const visibleReports = reports.filter((r) => {
    if (filter === "mine") return r.creator_id === userId;
    if (filter === "team")
      return r.is_public && !r.is_global && r.team_id === currentTeamId;
    if (filter === "global") return r.is_global;
    return true;
  });

  const canManage = detail !== null && canManageReport(detail);
  const refreshable =
    detail !== null &&
    canRefresh(detail, { userId, effectiveRole, currentTeamId }) &&
    refresh === null;
  const isRerun = detail !== null && refreshModeOf(detail) === "rerun";
  const runningElsewhere =
    detail !== null && detail.last_run_status === "running" && refresh === null;

  const handleCopyLink = async () => {
    if (!detail) return;
    try {
      await navigator.clipboard.writeText(publishedReportDeepLink(detail));
      setNotice("Link copied to clipboard");
    } catch (err) {
      console.error("[PublishedReports] clipboard copy failed:", err);
      setNotice(
        "Could not copy the link. Please copy it from the address bar."
      );
    }
  };

  const handleUnpublish = async () => {
    setConfirmUnpublish(false);
    if (!detail || userId === undefined) return;
    try {
      const input: UnpublishReportInput = { id: detail.id, user_id: userId };
      const result = await client.graphql({
        query: unpublishReport,
        variables: { input },
      });
      if (!result.data.unpublishReport) throw new Error("No report returned");
      setNotice("Report unpublished");
      setSelectedId(null);
      writeReportIdToUrl(null);
      await loadList();
    } catch (err) {
      console.error("[PublishedReports] unpublish failed:", err);
      setNotice(errorMessageOf(err, "Could not unpublish this report."));
    }
  };

  const handleEdited = (updated: PublishedReportSummary) => {
    setEditOpen(false);
    setDetail((prev) => (prev ? { ...prev, ...updated } : prev));
    setReports((prev) =>
      prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r))
    );
    setNotice("Report updated");
  };

  const renderMeta = (report: PublishedReport) => {
    const source = parsePublishedReportSource(report.source);
    const scope = source.request_scope ?? null;
    const tools = source.tools_used ?? [];
    const rerun = refreshModeOf(report) === "rerun";
    return (
      <Typography variant="caption" color="text.secondary" component="div">
        Published by <strong>{report.creator ?? report.creator_id}</strong> in{" "}
        <strong>{report.team_name ?? report.team_id}</strong> on{" "}
        {covertUtcToLocal(report.created_at)}. Model:{" "}
        <strong>{modelLabel(scope?.model_id)}</strong>
        {scope?.team_id !== undefined && scope?.team_id !== null && (
          <>
            {" "}
            · asked under team <strong>{scope.team_id}</strong>
          </>
        )}
        {" · Tools: "}
        <strong>{tools.length > 0 ? tools.join(", ") : "none recorded"}</strong>
        {!scope &&
          " · Provenance: unknown (answered before provenance stamping)"}
        {rerun && (
          <>
            {" · Last refreshed "}
            <strong>{covertUtcToLocal(report.published_at)}</strong>
            {` · ${report.run_count ?? 0} run${
              (report.run_count ?? 0) === 1 ? "" : "s"
            }`}
          </>
        )}
      </Typography>
    );
  };

  const renderRuns = () => (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ mt: 2, "&:before": { display: "none" } }}
      data-testid="report-runs"
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Run history{runs.length > 0 ? ` (${runs.length})` : ""}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0 }}>
        {runsLoading && runs.length === 0 ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="body2">Loading runs…</Typography>
          </Box>
        ) : runs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No runs recorded yet.
          </Typography>
        ) : (
          <List dense disablePadding>
            {runs.map((run) => (
              <ListItem key={run.id} disableGutters divider>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={runStatusLabel(run.status, run.error_type)}
                        color={
                          run.status === "succeeded"
                            ? "success"
                            : run.status === "failed"
                            ? "error"
                            : "default"
                        }
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                      <Typography variant="body2">
                        {covertUtcToLocal(run.started_at)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        by {run.triggered_by_name ?? run.triggered_by}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    run.status === "succeeded" && run.content_preview
                      ? run.content_preview.slice(0, 160)
                      : undefined
                  }
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box
      sx={{ display: "flex", gap: 2, minHeight: 480 }}
      data-testid="published-reports"
    >
      {/* List */}
      <Box sx={{ width: 340, minWidth: 280, flexShrink: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
            {FILTERS.map((f) => (
              <Chip
                key={f.id}
                label={f.label}
                size="small"
                color={filter === f.id ? "primary" : "default"}
                variant={filter === f.id ? "filled" : "outlined"}
                onClick={() => setFilter(f.id)}
                sx={{ cursor: "pointer" }}
              />
            ))}
          </Stack>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                onClick={loadList}
                disabled={listLoading}
                aria-label="refresh reports"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        {listError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {listError}
          </Alert>
        )}
        {listLoading && reports.length === 0 ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading reports…</Typography>
          </Box>
        ) : visibleReports.length === 0 && !listError ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {reports.length === 0
              ? "No published reports yet. Publish one from any completed Ask AI answer."
              : "No reports match this filter."}
          </Typography>
        ) : (
          <List
            dense
            disablePadding
            sx={{ maxHeight: "70vh", overflowY: "auto" }}
          >
            {visibleReports.map((r) => {
              const style = VISIBILITY_STYLE[visibilityOf(r)];
              return (
                <ListItemButton
                  key={r.id}
                  selected={r.id === selectedId}
                  onClick={() => handleSelect(r.id)}
                  sx={{
                    borderLeft: `4px solid ${style.borderColor}`,
                    backgroundColor: style.backgroundColor,
                    borderRadius: 1,
                    mb: 0.5,
                    alignItems: "flex-start",
                  }}
                >
                  <ListItemText
                    primary={
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ minWidth: 0 }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          noWrap
                          sx={{ flex: 1 }}
                        >
                          {r.title}
                        </Typography>
                        {refreshModeOf(r) === "rerun" && (
                          <Tooltip title="Refreshable report">
                            <AutorenewIcon
                              fontSize="inherit"
                              color="primary"
                              aria-label="refreshable"
                            />
                          </Tooltip>
                        )}
                        <Chip
                          size="small"
                          label={style.chipLabel}
                          variant="outlined"
                          sx={{ height: 20 }}
                        />
                      </Stack>
                    }
                    secondary={`${r.creator ?? r.creator_id} · ${
                      r.team_name ?? r.team_id
                    } · ${covertUtcToLocal(r.published_at)}`}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>

      <Divider orientation="vertical" flexItem />

      {/* Detail */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {notice && (
          <Alert severity="info" onClose={() => setNotice(null)} sx={{ mb: 1 }}>
            {notice}
          </Alert>
        )}
        {refreshError && (
          <Alert
            severity="warning"
            onClose={() => setRefreshError(null)}
            sx={{ mb: 1 }}
          >
            {refreshError}
          </Alert>
        )}
        {selectedId === null && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Select a report to read it.
          </Typography>
        )}
        {selectedId !== null && detailLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading report…</Typography>
          </Box>
        )}
        {selectedId !== null && !detailLoading && detailError && (
          <Alert severity="error">{detailError}</Alert>
        )}
        {selectedId !== null &&
          !detailLoading &&
          !detailError &&
          detailMissing && (
            <Alert severity="warning">
              This report is not available to you or was unpublished.
            </Alert>
          )}
        {detail && !detailLoading && (
          <Box>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={1}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ wordBreak: "break-word" }}
                >
                  {detail.title}
                </Typography>
                {detail.description && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                  >
                    {detail.description}
                  </Typography>
                )}
                <Box sx={{ mt: 0.5 }}>{renderMeta(detail)}</Box>
              </Box>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ flexShrink: 0 }}
              >
                {isRerun && (
                  <Chip
                    size="small"
                    icon={<AutorenewIcon />}
                    label={
                      refresh
                        ? `Refreshing… ${formatElapsed(elapsed)}`
                        : runningElsewhere
                        ? "Refreshing…"
                        : "Refreshable"
                    }
                    color="primary"
                    variant="outlined"
                  />
                )}
                <Chip
                  size="small"
                  label={VISIBILITY_STYLE[visibilityOf(detail)].chipLabel}
                  variant="outlined"
                />
                <Tooltip title="Copy link">
                  <IconButton
                    size="small"
                    onClick={handleCopyLink}
                    aria-label="Copy report link"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {isRerun && canManage && (
                  <Tooltip
                    title={
                      refresh || runningElsewhere
                        ? "A refresh is in progress"
                        : "Refresh now: re-run the report question"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleRefreshNow}
                        disabled={!refreshable}
                        aria-label="Refresh report now"
                      >
                        {refresh ? (
                          <CircularProgress size={16} />
                        ) : (
                          <AutorenewIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {canManage && (
                  <>
                    <Tooltip title="Edit title, description or visibility">
                      <IconButton
                        size="small"
                        onClick={() => setEditOpen(true)}
                        aria-label="Edit report"
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Unpublish">
                      <IconButton
                        size="small"
                        onClick={() => setConfirmUnpublish(true)}
                        aria-label="Unpublish report"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Stack>
            </Stack>

            {isRerun &&
              detail.last_run_status === "failed" &&
              !refreshError && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  The last refresh
                  {detail.last_run_at
                    ? ` on ${covertUtcToLocal(detail.last_run_at)}`
                    : ""}{" "}
                  failed. Showing the answer from{" "}
                  {covertUtcToLocal(detail.published_at)}.
                </Alert>
              )}

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="subtitle2" color="text.secondary">
              Question
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
              {detail.question}
            </Typography>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 0.5 }}
            >
              Answer
            </Typography>
            <Box
              className="ChatBoxComponent-message AI"
              sx={{ p: 1.5, borderRadius: 2 }}
            >
              <AnswerTableDownloads
                html={(parse(detail.content ?? "") as string).replace(
                  />\s+</g,
                  "><"
                )}
                messageIndex={0}
                conversationId={`published-report-${detail.id}`}
              />
            </Box>
            {isRerun && renderRuns()}
          </Box>
        )}
      </Box>

      <ConfirmationDialog
        isOpen={confirmUnpublish}
        onConfirm={handleUnpublish}
        onClose={() => setConfirmUnpublish(false)}
        message="Unpublish this report? It will disappear from every list; the original conversation is not affected."
      />
      {detail && (
        <EditPublishedReportDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          report={detail}
          userId={userId ?? 0}
          effectiveRole={effectiveRole}
          onSaved={handleEdited}
        />
      )}
    </Box>
  );
};

export default PublishedReportsContent;
